import { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { MessageSquare, Plus, User as UserIcon, MessageCircle, X, Calendar } from "lucide-react";
import { toast } from "sonner";
import Header from "../components/Header";
import BottomNav from "../components/BottomNav";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Forum() {
  const { t, i18n } = useTranslation();
  const dateLocale = i18n.language === "fr" ? "fr-FR" : "it-IT";
  const CATEGORIES = t("forum.categories", { returnObjects: true });
  const navigate = useNavigate();
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [newThread, setNewThread] = useState({ title: "", category: CATEGORIES[CATEGORIES.length - 1], content: "" });
  const [posting, setPosting] = useState(false);
  const [filter, setFilter] = useState("all");

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/forum/threads`, { withCredentials: true });
      setThreads(r.data);
    } catch {
      toast.error(t("forum.loadError"));
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!newThread.title.trim() || !newThread.content.trim()) {
      toast.error(t("forum.titleRequired")); return;
    }
    setPosting(true);
    try {
      await axios.post(`${API}/forum/posts`, {
        title: newThread.title,
        category: newThread.category,
        content: newThread.content,
      }, { withCredentials: true });
      toast.success(t("forum.postSuccess"));
      setNewThread({ title: "", category: CATEGORIES[CATEGORIES.length - 1], content: "" });
      setShowNew(false);
      load();
    } catch { toast.error(t("forum.postError")); }
    finally { setPosting(false); }
  };

  const filtered = filter === "all" ? threads : threads.filter((thread) => thread.category === filter);

  const fmtDate = (iso) => {
    const d = new Date(iso);
    const now = new Date();
    const diffH = (now - d) / 36e5;
    if (diffH < 1) return t("forum.dateMinAgo", { count: Math.round(diffH * 60) });
    if (diffH < 24) return t("forum.dateHoursAgo", { count: Math.round(diffH) });
    if (diffH < 24 * 7) return t("forum.dateDaysAgo", { count: Math.round(diffH / 24) });
    return d.toLocaleDateString(dateLocale, { day: "2-digit", month: "short", year: "numeric" });
  };

  return (
    <div className="min-h-screen pb-24" data-testid="forum-page">
      <Header title={t("forum.headerTitle")} subtitle={t("forum.headerSubtitle")} />
      <main className="max-w-2xl mx-auto px-4 py-5">
        {/* Intro */}
        <section className="vs-card p-4 flex items-center gap-3 mb-4"
          style={{ background: "var(--gradient-vine)", color: "white", borderColor: "transparent" }}>
          <div className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0"
            style={{ background: "rgba(255,255,255,0.18)", border: "2px solid rgba(255,255,255,0.3)" }}>
            <MessageSquare size={22} color="white" />
          </div>
          <div className="flex-1">
            <h2 className="heading text-lg font-extrabold leading-tight">{t("forum.introTitle")}</h2>
            <p className="text-xs" style={{ color: "rgba(255,255,255,0.85)" }}>
              {t("forum.introSubtitle")}
            </p>
          </div>
          <button onClick={() => setShowNew(true)}
            className="px-3 py-2 rounded-lg font-semibold text-sm inline-flex items-center gap-1 shrink-0"
            style={{ background: "var(--gold)", color: "#3D2300", border: "2px solid #7A4B00" }}
            data-testid="new-thread-btn">
            <Plus size={14} /> {t("forum.newButton")}
          </button>
        </section>

        {/* Filter */}
        <div className="flex gap-1.5 mb-4 overflow-x-auto pb-1">
          <button onClick={() => setFilter("all")}
            className="px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap"
            style={{
              background: filter === "all" ? "var(--primary)" : "var(--surface)",
              color: filter === "all" ? "white" : "var(--text-primary)",
              border: `2px solid ${filter === "all" ? "var(--primary)" : "var(--border)"}`,
            }}>
            {t("forum.filterAll")}
          </button>
          {CATEGORIES.map((c) => (
            <button key={c} onClick={() => setFilter(c)}
              className="px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap"
              style={{
                background: filter === c ? "var(--primary)" : "var(--surface)",
                color: filter === c ? "white" : "var(--text-primary)",
                border: `2px solid ${filter === c ? "var(--primary)" : "var(--border)"}`,
              }}>
              {c}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex justify-center py-10"><div className="vs-spinner" /></div>
        ) : filtered.length === 0 ? (
          <div className="vs-card p-8 text-center">
            <MessageSquare size={42} style={{ color: "var(--text-muted)" }} className="mx-auto mb-3" />
            <p className="font-semibold">{t("forum.emptyTitle")}</p>
            <p className="text-sm mt-1" style={{ color: "var(--text-muted)" }}>
              {t("forum.emptySubtitle")}
            </p>
            <button onClick={() => setShowNew(true)} className="vs-btn-primary mt-4"
              style={{ background: "var(--gradient-vine)", borderColor: "var(--primary)" }}>
              <Plus size={16} className="inline mr-1" /> {t("forum.openThreadButton")}
            </button>
          </div>
        ) : (
          <div className="space-y-2">
            {filtered.map((thread) => (
              <button key={thread.id} onClick={() => navigate(`/forum/${thread.id}`)}
                className="vs-card p-4 w-full text-left transition-transform hover:scale-[1.01] vs-fade-up"
                data-testid={`thread-${thread.id}`}>
                <div className="flex items-start gap-3">
                  {thread.author_picture ? (
                    <img src={thread.author_picture} alt={thread.author_name}
                      className="w-10 h-10 rounded-full shrink-0 object-cover"
                      referrerPolicy="no-referrer" />
                  ) : (
                    <div className="w-10 h-10 rounded-full shrink-0 flex items-center justify-center font-bold text-sm"
                      style={{ background: "var(--primary)", color: "white" }}>
                      {(thread.author_name || "?").charAt(0).toUpperCase()}
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className="vs-pill text-xs"
                        style={{ background: "var(--grape)", color: "white", borderColor: "transparent" }}>
                        {thread.category || CATEGORIES[CATEGORIES.length - 1]}
                      </span>
                      <span className="text-xs" style={{ color: "var(--text-muted)" }}>
                        <Calendar size={11} className="inline mr-1" />{fmtDate(thread.created_at)}
                      </span>
                    </div>
                    <div className="heading font-bold text-base mb-1" style={{ color: "var(--text-primary)" }}>
                      {thread.title}
                    </div>
                    <div className="text-sm line-clamp-2" style={{ color: "var(--text-secondary)" }}>
                      {thread.content}
                    </div>
                    <div className="flex items-center gap-3 mt-2 text-xs" style={{ color: "var(--text-muted)" }}>
                      <span><UserIcon size={11} className="inline mr-1" />{thread.author_name}</span>
                      <span><MessageCircle size={11} className="inline mr-1" />{t("forum.replyCount", { count: thread.reply_count })}</span>
                    </div>
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </main>

      {/* New thread modal */}
      {showNew && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center"
          style={{ background: "rgba(17,20,17,0.55)" }}
          onClick={() => !posting && setShowNew(false)}>
          <div className="w-full sm:max-w-lg vs-card p-5"
            style={{ borderRadius: "16px 16px 0 0", maxHeight: "92vh", overflowY: "auto" }}
            onClick={(e) => e.stopPropagation()}
            data-testid="new-thread-form">
            <div className="flex justify-between items-center mb-4">
              <h3 className="heading text-xl font-bold">{t("forum.newThreadModalTitle")}</h3>
              <button onClick={() => setShowNew(false)} className="p-2"><X size={20} /></button>
            </div>
            <form onSubmit={submit} className="space-y-3">
              <div>
                <label className="vs-label">{t("forum.titleLabel")}</label>
                <input className="vs-input" value={newThread.title}
                  onChange={(e) => setNewThread({ ...newThread, title: e.target.value })}
                  placeholder={t("forum.titlePlaceholder")}
                  maxLength={120}
                  data-testid="thread-title" required />
              </div>
              <div>
                <label className="vs-label">{t("forum.categoryLabel")}</label>
                <select className="vs-input" value={newThread.category}
                  onChange={(e) => setNewThread({ ...newThread, category: e.target.value })}
                  data-testid="thread-category">
                  {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="vs-label">{t("forum.messageLabel")}</label>
                <textarea className="vs-input" rows="5" value={newThread.content}
                  onChange={(e) => setNewThread({ ...newThread, content: e.target.value })}
                  placeholder={t("forum.messagePlaceholder")}
                  maxLength={2000}
                  data-testid="thread-content" required />
                <div className="text-xs mt-1 text-right" style={{ color: "var(--text-muted)" }}>
                  {newThread.content.length}/2000
                </div>
              </div>
              <div className="flex gap-2 pt-2">
                <button type="button" className="vs-btn-secondary flex-1" onClick={() => setShowNew(false)} disabled={posting}>
                  {t("dashboard.form.cancelButton")}
                </button>
                <button type="submit" className="vs-btn-primary flex-1" disabled={posting}
                  style={{ background: "var(--gradient-vine)", borderColor: "var(--primary)" }}
                  data-testid="thread-submit">
                  {posting ? t("forum.publishing") : t("forum.publishButton")}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <BottomNav />
    </div>
  );
}
